/**
 * @deprecated Use preparation-status-service.ts instead
 *
 * DEPRECATION NOTICE:
 * This service only checks employee assignment, NOT opening hours coverage.
 * For accurate preparation status that checks BOTH conditions, use:
 * - Service: @/lib/scheduling/preparation-status-service.ts
 * - Hook: @/hooks/scheduling/usePreparationStatus.ts
 *
 * Original description:
 * Optimized Period Statistics Service
 * Single-query approach for fast period status calculation
 */

import { supabase } from '@/integrations/supabase/client';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  addMonths
} from 'date-fns';
import { PeriodStatuses } from '@/types/insights';

/**
 * Fetch period statistics with a single optimized database query
 * Much faster than the old approach (4 sequential queries + complex validation)
 */
export const fetchPeriodStats = async (businessId: string): Promise<PeriodStatuses> => {
  const now = new Date();

  // Calculate all period boundaries
  const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
  const currentWeekEnd = endOfWeek(now, { weekStartsOn: 1 });

  const nextWeekStart = startOfWeek(addDays(now, 7), { weekStartsOn: 1 });
  const nextWeekEnd = endOfWeek(addDays(now, 7), { weekStartsOn: 1 });

  const currentMonthStart = startOfMonth(now);
  const currentMonthEnd = endOfMonth(now);

  const nextMonthStart = startOfMonth(addMonths(now, 1));
  const nextMonthEnd = endOfMonth(addMonths(now, 1));

  // Single query to fetch ALL shifts across all periods
  const { data, error } = await supabase
    .from('shifts')
    .select('id, employee_id, start_time')
    .eq('business_id', businessId)
    .gte('start_time', currentWeekStart.toISOString())
    .lte('start_time', nextMonthEnd.toISOString());

  if (error) {
    console.error('Error fetching period stats:', error);
    throw error;
  }

  // Initialize stats for all periods
  const stats: PeriodStatuses = {
    currentWeek: { hasShifts: false, allAssigned: true, shiftsCount: 0 },
    nextWeek: { hasShifts: false, allAssigned: true, shiftsCount: 0 },
    currentMonth: { hasShifts: false, allAssigned: true, shiftsCount: 0 },
    nextMonth: { hasShifts: false, allAssigned: true, shiftsCount: 0 }
  };

  // Single pass through shifts to categorize into periods
  data?.forEach(shift => {
    const shiftDate = new Date(shift.start_time);
    const hasEmployee = !!shift.employee_id;

    // Check which periods this shift belongs to
    // Note: A shift can belong to multiple periods (e.g., week + month)

    if (shiftDate >= currentWeekStart && shiftDate <= currentWeekEnd) {
      stats.currentWeek.shiftsCount++;
      stats.currentWeek.hasShifts = true;
      if (!hasEmployee) stats.currentWeek.allAssigned = false;
    }

    if (shiftDate >= nextWeekStart && shiftDate <= nextWeekEnd) {
      stats.nextWeek.shiftsCount++;
      stats.nextWeek.hasShifts = true;
      if (!hasEmployee) stats.nextWeek.allAssigned = false;
    }

    if (shiftDate >= currentMonthStart && shiftDate <= currentMonthEnd) {
      stats.currentMonth.shiftsCount++;
      stats.currentMonth.hasShifts = true;
      if (!hasEmployee) stats.currentMonth.allAssigned = false;
    }

    if (shiftDate >= nextMonthStart && shiftDate <= nextMonthEnd) {
      stats.nextMonth.shiftsCount++;
      stats.nextMonth.hasShifts = true;
      if (!hasEmployee) stats.nextMonth.allAssigned = false;
    }
  });

  return stats;
};

/**
 * Calculate total planned shifts (current + next month)
 * Helper function for backward compatibility
 */
export const calculatePlannedShifts = (stats: PeriodStatuses): number => {
  return stats.currentMonth.shiftsCount + stats.nextMonth.shiftsCount;
};

/**
 * Check if all shifts are assigned (current + next month)
 * Helper function for backward compatibility
 */
export const calculateAllShiftsAssigned = (stats: PeriodStatuses): boolean => {
  const totalShifts = stats.currentMonth.shiftsCount + stats.nextMonth.shiftsCount;
  if (totalShifts === 0) return true; // No shifts = vacuously true
  return stats.currentMonth.allAssigned && stats.nextMonth.allAssigned;
};
